import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Briefcase, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Building2,
  MapPin,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { jobDescriptionsAPI } from '../../lib/api';

const NIVEIS = [
  { value: 'junior', label: 'Júnior' },
  { value: 'pleno', label: 'Pleno' },
  { value: 'senior', label: 'Sênior' },
  { value: 'especialista', label: 'Especialista' },
];

const MODALIDADES = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'hibrido', label: 'Híbrido' },
  { value: 'remoto', label: 'Remoto' },
];

export default function JobDescriptions() {
  const navigate = useNavigate();
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCargo, setEditingCargo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cargoToDelete, setCargoToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    titulo: '',
    nivel: 'pleno',
    descricao: '',
    requisitos: [],
    responsabilidades: [],
    competencias: [],
    faixa_salarial_min: '',
    faixa_salarial_max: '',
    carga_horaria: '',
    modalidade: 'presencial',
  });
  
  // Temp state for adding items to lists
  const [newRequisito, setNewRequisito] = useState('');
  const [newResponsabilidade, setNewResponsabilidade] = useState('');
  const [newCompetencia, setNewCompetencia] = useState('');

  useEffect(() => {
    fetchCargos();
  }, []);

  const fetchCargos = async () => {
    try {
      setLoading(true);
      const res = await jobDescriptionsAPI.getAll();
      setCargos(res.data.job_descriptions || []);
    } catch (err) {
      toast.error('Erro ao carregar cargos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cargo = null) => {
    if (cargo) {
      setEditingCargo(cargo);
      setFormData({
        titulo: cargo.titulo || '',
        nivel: cargo.nivel || 'pleno',
        descricao: cargo.descricao || '',
        requisitos: cargo.requisitos || [],
        responsabilidades: cargo.responsabilidades || [],
        competencias: cargo.competencias || [],
        faixa_salarial_min: cargo.faixa_salarial_min || '',
        faixa_salarial_max: cargo.faixa_salarial_max || '',
        carga_horaria: cargo.carga_horaria || '',
        modalidade: cargo.modalidade || 'presencial',
      });
    } else {
      setEditingCargo(null);
      setFormData({
        titulo: '',
        nivel: 'pleno',
        descricao: '',
        requisitos: [],
        responsabilidades: [],
        competencias: [],
        faixa_salarial_min: '',
        faixa_salarial_max: '',
        carga_horaria: '',
        modalidade: 'presencial',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.titulo.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        ...formData,
        faixa_salarial_min: formData.faixa_salarial_min ? parseFloat(formData.faixa_salarial_min) : null,
        faixa_salarial_max: formData.faixa_salarial_max ? parseFloat(formData.faixa_salarial_max) : null,
      };

      if (editingCargo) {
        await jobDescriptionsAPI.update(editingCargo.id, payload);
        toast.success('Cargo atualizado com sucesso');
      } else {
        await jobDescriptionsAPI.create(payload);
        toast.success('Cargo criado com sucesso');
      }

      setShowModal(false);
      fetchCargos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar cargo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!cargoToDelete) return;

    try {
      setSubmitting(true);
      await jobDescriptionsAPI.delete(cargoToDelete.id);
      toast.success('Cargo removido com sucesso');
      setShowDeleteDialog(false);
      setCargoToDelete(null);
      fetchCargos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover cargo');
    } finally {
      setSubmitting(false);
    }
  };

  const addItemToList = (field, value, setter) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));
    setter('');
  };

  const removeItemFromList = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const filteredCargos = cargos.filter(c => 
    c.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.nivel?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getNivelBadgeColor = (nivel) => {
    const colors = {
      junior: 'bg-green-100 text-green-800',
      pleno: 'bg-blue-100 text-blue-800',
      senior: 'bg-purple-100 text-purple-800',
      especialista: 'bg-orange-100 text-orange-800',
    };
    return colors[nivel] || 'bg-slate-100 text-slate-800';
  };

  const getModalidadeBadgeColor = (modalidade) => {
    const colors = {
      presencial: 'bg-slate-100 text-slate-800',
      hibrido: 'bg-cyan-100 text-cyan-800',
      remoto: 'bg-emerald-100 text-emerald-800',
    };
    return colors[modalidade] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-cyan-600" />
            Descrições de Cargos
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Gerencie os cargos da sua empresa para facilitar contratações e importações
          </p>
        </div>
        <Button 
          onClick={() => handleOpenModal()} 
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="btn-novo-cargo"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Cargo
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar por título ou nível..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-cargos"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{cargos.length}</div>
            <div className="text-sm text-slate-500">Total de Cargos</div>
          </CardContent>
        </Card>
        {NIVEIS.map(nivel => (
          <Card key={nivel.value} className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-900">
                {cargos.filter(c => c.nivel === nivel.value).length}
              </div>
              <div className="text-sm text-slate-500">{nivel.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lista de Cargos */}
      {filteredCargos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {searchTerm ? 'Nenhum cargo encontrado' : 'Nenhum cargo cadastrado'}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchTerm 
                ? 'Tente uma busca diferente' 
                : 'Crie descrições de cargos para padronizar contratações'
              }
            </p>
            {!searchTerm && (
              <Button onClick={() => handleOpenModal()} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Cargo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCargos.map((cargo) => (
            <Card 
              key={cargo.id} 
              className="border-slate-200 hover:border-cyan-200 transition-colors"
              data-testid={`cargo-card-${cargo.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{cargo.titulo}</h3>
                      <Badge className={getNivelBadgeColor(cargo.nivel)}>
                        {NIVEIS.find(n => n.value === cargo.nivel)?.label || cargo.nivel}
                      </Badge>
                      <Badge className={getModalidadeBadgeColor(cargo.modalidade)}>
                        {MODALIDADES.find(m => m.value === cargo.modalidade)?.label || cargo.modalidade}
                      </Badge>
                      {cargo.ativo === false && (
                        <Badge variant="destructive">Inativo</Badge>
                      )}
                    </div>
                    
                    {cargo.descricao && (
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">{cargo.descricao}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {cargo.carga_horaria && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {cargo.carga_horaria}h/semana
                        </span>
                      )}
                      {(cargo.faixa_salarial_min || cargo.faixa_salarial_max) && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {cargo.faixa_salarial_min && `R$ ${cargo.faixa_salarial_min.toLocaleString('pt-BR')}`}
                          {cargo.faixa_salarial_min && cargo.faixa_salarial_max && ' - '}
                          {cargo.faixa_salarial_max && `R$ ${cargo.faixa_salarial_max.toLocaleString('pt-BR')}`}
                        </span>
                      )}
                      {cargo.requisitos?.length > 0 && (
                        <span>{cargo.requisitos.length} requisitos</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setExpandedCard(expandedCard === cargo.id ? null : cargo.id)}
                    >
                      {expandedCard === cargo.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenModal(cargo)}
                      data-testid={`btn-edit-${cargo.id}`}
                    >
                      <Edit2 className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setCargoToDelete(cargo);
                        setShowDeleteDialog(true);
                      }}
                      data-testid={`btn-delete-${cargo.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {/* Expanded content */}
                {expandedCard === cargo.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100 grid md:grid-cols-3 gap-4">
                    {cargo.requisitos?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Requisitos</h4>
                        <ul className="space-y-1">
                          {cargo.requisitos.map((req, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {cargo.responsabilidades?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Responsabilidades</h4>
                        <ul className="space-y-1">
                          {cargo.responsabilidades.map((resp, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {cargo.competencias?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Competências</h4>
                        <div className="flex flex-wrap gap-2">
                          {cargo.competencias.map((comp, i) => (
                            <Badge key={i} variant="outline">{comp}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criação/Edição */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCargo ? 'Editar Cargo' : 'Novo Cargo'}
            </DialogTitle>
            <DialogDescription>
              {editingCargo 
                ? 'Atualize as informações do cargo' 
                : 'Preencha os dados para criar uma nova descrição de cargo'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Título e Nível */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="titulo">Título *</Label>
                <Input
                  id="titulo"
                  value={formData.titulo}
                  onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Ex: Analista de RH"
                  data-testid="input-titulo"
                />
              </div>
              <div>
                <Label htmlFor="nivel">Nível</Label>
                <Select
                  value={formData.nivel}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, nivel: v }))}
                >
                  <SelectTrigger data-testid="select-nivel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NIVEIS.map(n => (
                      <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Modalidade e Carga Horária */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="modalidade">Modalidade</Label>
                <Select
                  value={formData.modalidade}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, modalidade: v }))}
                >
                  <SelectTrigger data-testid="select-modalidade">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODALIDADES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="carga_horaria">Carga Horária (h/semana)</Label>
                <Input
                  id="carga_horaria"
                  type="number"
                  value={formData.carga_horaria}
                  onChange={(e) => setFormData(prev => ({ ...prev, carga_horaria: e.target.value }))}
                  placeholder="44"
                />
              </div>
            </div>

            {/* Faixa Salarial */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="faixa_min">Salário Mínimo (R$)</Label>
                <Input
                  id="faixa_min"
                  type="number"
                  value={formData.faixa_salarial_min}
                  onChange={(e) => setFormData(prev => ({ ...prev, faixa_salarial_min: e.target.value }))}
                  placeholder="3000"
                />
              </div>
              <div>
                <Label htmlFor="faixa_max">Salário Máximo (R$)</Label>
                <Input
                  id="faixa_max"
                  type="number"
                  value={formData.faixa_salarial_max}
                  onChange={(e) => setFormData(prev => ({ ...prev, faixa_salarial_max: e.target.value }))}
                  placeholder="5000"
                />
              </div>
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva as principais atividades e responsabilidades do cargo..."
                rows={3}
                data-testid="input-descricao"
              />
            </div>

            {/* Requisitos */}
            <div>
              <Label>Requisitos</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newRequisito}
                  onChange={(e) => setNewRequisito(e.target.value)}
                  placeholder="Adicionar requisito..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addItemToList('requisitos', newRequisito, setNewRequisito);
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => addItemToList('requisitos', newRequisito, setNewRequisito)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.requisitos.map((req, i) => (
                  <Badge key={i} variant="secondary" className="py-1">
                    {req}
                    <button
                      type="button"
                      onClick={() => removeItemFromList('requisitos', i)}
                      className="ml-2 hover:text-red-500"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Responsabilidades */}
            <div>
              <Label>Responsabilidades</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newResponsabilidade}
                  onChange={(e) => setNewResponsabilidade(e.target.value)}
                  placeholder="Adicionar responsabilidade..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addItemToList('responsabilidades', newResponsabilidade, setNewResponsabilidade);
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => addItemToList('responsabilidades', newResponsabilidade, setNewResponsabilidade)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.responsabilidades.map((resp, i) => (
                  <Badge key={i} variant="secondary" className="py-1">
                    {resp}
                    <button
                      type="button"
                      onClick={() => removeItemFromList('responsabilidades', i)}
                      className="ml-2 hover:text-red-500"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Competências */}
            <div>
              <Label>Competências</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newCompetencia}
                  onChange={(e) => setNewCompetencia(e.target.value)}
                  placeholder="Adicionar competência..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addItemToList('competencias', newCompetencia, setNewCompetencia);
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => addItemToList('competencias', newCompetencia, setNewCompetencia)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.competencias.map((comp, i) => (
                  <Badge key={i} variant="secondary" className="py-1">
                    {comp}
                    <button
                      type="button"
                      onClick={() => removeItemFromList('competencias', i)}
                      className="ml-2 hover:text-red-500"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="btn-salvar-cargo"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {editingCargo ? 'Salvar Alterações' : 'Criar Cargo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o cargo "{cargoToDelete?.titulo}"?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={submitting}
              data-testid="btn-confirmar-delete"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
